import { Component, Emit, Inject, Prop, Watch } from 'vue-property-decorator';
import ApeironComponent from '@/components/commons/apeiron-component.model';
import { FiltroParticipacion } from '@/shared/model/filtro-participacion.model';
import ParticipanteService from '@/components/participantes/participante.service';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import { StatusCodes } from 'http-status-codes';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import {
  ITiempoDedicacion,
  ParticipacionSnp,
  MenuInstitucionSnp,
  MenuSedeSnp,
  MenuTrabaja,
  MenuEmpleadoEn,
  MenuTipoBeca,
} from '@/shared/model/snp/alumno.model';
import CatalogosSnp from '@/shared/model/snp/catalogos-snp.factory';
import BecaService from '@/entities/seguimiento/beca.service';

@Component
export default class MatriculaListadoComponent extends ApeironComponent {
  @Inject('participanteService') private readonly participanteService: () => ParticipanteService;
  @Inject('becaService') private becaService: () => BecaService;

  @Prop({ required: true })
  public solucion: Solucion;

  @Prop({ required: true })
  public solicitud: Solicitud;

  public tiposBeca: Array<MenuTipoBeca> = [];

  @Prop({
    required: false,
    default: function () {
      return false;
    },
  })
  public actualizarTabla: boolean;

  public perPage = 15;
  public currentPage = 1;
  public filtroAlumnos = '';
  public alumnosFiltrados: Array<ParticipacionSnp> = [];
  public alumnos: Array<ParticipacionSnp> = [];

  public alumnoSeleccionado: ParticipacionSnp = new ParticipacionSnp();
  public mostrarModalDetalle = false;
  public mostrarModalEliminar = false;

  public tiemposDedicacion: Array<ITiempoDedicacion> = [];
  public instituciones: Array<MenuInstitucionSnp> = [];
  public sedes: Array<MenuSedeSnp> = [];
  public opcionesTrabaja: Array<MenuTrabaja> = [];
  public opcionesEmpleadoEn: Array<MenuEmpleadoEn> = [];

  public opcionesRadios = [
    { text: this.$t('apeironGwApp.matriculaGpln.form.si'), value: true },
    { text: this.$t('apeironGwApp.matriculaGpln.form.no'), value: false },
  ];

  public readonly fieldsAlumnos = [
    {
      key: 'participante.cvu',
      label: this.$t('apeironGwApp.matriculaGpln.form.cvuAlumno'),
      class: 'text-left',
      thStyle: { width: '8rem' },
    },
    {
      key: 'participante',
      label: this.$t('apeironGwApp.matriculaGpln.form.nombreAlumno'),
      class: 'text-left',
      thStyle: { width: '15rem' },
    },
    {
      key: 'tiempoDedicacion',
      label: this.$t('apeironGwApp.matriculaGpln.form.tiempoDedicacion'),
      class: 'text-left',
      thStyle: { width: '10rem' },
    },
    {
      key: 'tipoParticipacion',
      label: this.$t('apeironGwApp.matriculaGpln.form.tipoParticipacion'),
      class: 'text-left',
      thStyle: { width: '10rem' },
    },
    {
      key: 'participante.estadoSolicitudBeca',
      class: 'text-left',
      thStyle: { width: '8rem' },
      label: this.$t('apeironGwApp.persona.participacion.becario.estadoSolicitudBeca'),
    },
    {
      key: 'estadoBeca',
      class: 'text-left',
      thStyle: { width: '8rem' },
      label: this.$t('apeironGwApp.persona.participacion.becario.estadoBeca'),
    },
    {
      key: 'acciones',
      label: this.$t('apeironGwApp.matriculaGpln.acciones.acciones'),
      class: 'text-center btn-acciones',
      thStyle: { width: '15rem' },
    },
  ];

  private mounted(): void {
    this.tiposBeca.push({
      value: { clave: 'POSTULANTE_BECA', descripcion: 'Postulante a Beca SECIHTI' },
      text: 'Postulante a Beca SECIHTI',
    });
    this.iniciarComponente();
  }

  @Watch('actualizarTabla')
  private refrescarTabla(): void {
    this.iniciarComponente();
  }

  private iniciarComponente(): void {
    this.tiemposDedicacion = CatalogosSnp.obtenerTiemposDedicacionAlumnos();
    this.instituciones = [];
    if (this.solicitud?.programa?.instituciones) {
      this.solicitud.programa.instituciones.forEach(institucion => {
        this.instituciones.push({
          value: {
            claveInstitucion: institucion.claveInstitucion,
            nombreInstitucion: institucion.nombreInstitucion,
          },
          text: institucion.nombreInstitucion,
        });
      });
    }
    this.opcionesTrabaja = [];
    CatalogosSnp.obtenerOpcionesDeTrabaja().forEach(opcion => {
      this.opcionesTrabaja.push({ value: opcion, text: opcion.descripcion });
    });
    this.opcionesEmpleadoEn = [];
    CatalogosSnp.obtenerOpcionesDeEmpleadoEn().forEach(opcion => {
      this.opcionesEmpleadoEn.push({ value: opcion, text: opcion.descripcion });
    });

    this.obtenerAlumnos();
  }

  public obtenerAlumnos(): void {
    const filtroPart: FiltroParticipacion = {
      solicitudId: this.solicitud.id,
      rol: RolAutoridad.ALUMNO,
    };
    const paginationQuery = {
      page: 0,
      size: 2000,
      sort: ['id,desc'],
      filter: filtroPart,
    };
    this.participanteService()
      .obtenerParticipaciones(paginationQuery)
      .then(res => {
        this.alumnos = [];
        (res.data ?? []).forEach(item => {
          const participante = ParticipacionSnp.fromObject(item);
          if (participante.participante?.idParticipacionOrigen) {
            this.becaService()
              .findByParticipacionId(participante.participante.idParticipacionOrigen)
              .then(beca => {
                participante.estadoBeca = beca?.estadoActual;
              });
          }
          this.alumnos.push(participante);
        });
        this.buscarEnAlumnos();
      })
      .catch(err => {
        if (err.response.data.status !== StatusCodes.NOT_FOUND) {
          this.alertService().showHttpError(this, err.response);
        }
      });
  }

  public eliminarAlumno(): void {
    if (!this.readOnly) {
      this.participanteService()
        .delete(this.alumnoSeleccionado.id)
        .then(() => {
          this.mostrarModalEliminar = false;
          this.obtenerAlumnos();
          this.alertService().showSuccess(this, this.$t('apeironGwApp.matriculaGpln.modal.alumnoEliminado').toString());
        })
        .catch(error => {
          this.alertService().showHttpError(this, error.response);
        });
    } else {
      this.mostrarModalEliminar = false;
    }
  }

  public buscarEnAlumnos(): void {
    this.alumnosFiltrados = [];
    this.alumnos.forEach(alumno => {
      if (this.filtroAlumnos) {
        if (
          alumno.participante.cvu?.includes(this.filtroAlumnos) ||
          alumno.participante.nombre?.toLowerCase().includes(this.filtroAlumnos.toLowerCase()) ||
          alumno.participante.apellidoPaterno?.toLowerCase().includes(this.filtroAlumnos.toLowerCase()) ||
          alumno.participante.apellidoMaterno?.toLowerCase().includes(this.filtroAlumnos.toLowerCase())
        ) {
          this.alumnosFiltrados.push(alumno);
        }
      } else {
        this.alumnosFiltrados.push(alumno);
      }
    });
  }

  public mostrarModalDetalleAlumno(alumnoPrograma: ParticipacionSnp): void {
    this.mostrarModalDetalle = true;
    this.alumnoSeleccionado = alumnoPrograma;
    this.alumnoSeleccionado.participante.duracionMeses = this.solicitud?.programa?.planDeEstudios?.duracionOficial ?? 0;

    this.seleccionarInstitucion();
  }

  public editarAlumno(alumnoPrograma: ParticipacionSnp): void {
    this.alumnoSeleccionado = alumnoPrograma;
    this.editarAlumnoEmitir(this.alumnoSeleccionado);
  }

  public mostrarModalEliminarAlumno(alumno: ParticipacionSnp): void {
    this.mostrarModalEliminar = true;
    this.alumnoSeleccionado = ParticipacionSnp.fromObject(alumno);
  }

  public seleccionarInstitucion(): void {
    this.sedes = [];
    if (this.solicitud?.programa?.instituciones) {
      this.solicitud.programa.instituciones.forEach(institucion => {
        if (institucion.claveInstitucion == this.alumnoSeleccionado.participante['institucion']?.claveInstitucion) {
          institucion.sedes.forEach(sede => {
            this.sedes.push({
              value: {
                claveSede: sede.claveSede,
                claveDependencia: sede.claveDependencia,
                nombreDependencia: sede.nombreDependencia,
                sede: sede.sede,
              },
              text: `${sede.nombreDependencia} (${sede.sede})`,
            });
          });
        }
      });
    }
  }

  @Emit('editarAlumno')
  public editarAlumnoEmitir(alumno: ParticipacionSnp): ParticipacionSnp {
    const alumnoClonado = ParticipacionSnp.fromObject(alumno);

    this.buscarEnAlumnos();

    return alumnoClonado;
  }

  get registros(): number {
    return this.alumnosFiltrados?.length ?? 0;
  }

  public decodeTiempoParticipacion(id: string): string {
    return this.tiemposDedicacion.find(tiempo => tiempo.value == id).text;
  }
}
